// Shared Batch Creation core — pure, module-agnostic primitives.
// Reused later by Cleaning / Checklists / Prep / Supplier Products. NO module fields,
// NO business/security rules here (those live in each module + server-side).

export const ROW_STATUS = {
  DRAFT: "draft", VALID: "valid", WARNING: "warning", INVALID: "invalid",
  CREATED: "created", DUPLICATE: "duplicate", FAILED: "failed",
};

export const DEFAULT_MAX_BATCH = 200; // onboarding-sized; module routes may override with rationale

let _seq = 0;
export function makeRow(values = {}, position = 0) {
  _seq += 1;
  return {
    rowId: `r${Date.now().toString(36)}${_seq}`,
    position,
    values: { ...values },
    errors: {},      // field -> message
    warnings: [],     // row-level warnings (e.g. possible duplicate)
    status: ROW_STATUS.DRAFT,
    result: null,     // { record_id, code, message } after reconcile
  };
}

// --- Paste parsing -------------------------------------------------------
// Newline paste → one row per non-blank line into `column`. Handles LF/CRLF,
// trims, drops blank lines, preserves order.
export function parseNewline(text, column) {
  return String(text ?? "")
    .replace(/\r\n/g, "\n").replace(/\r/g, "\n")
    .split("\n").map((l) => l.trim()).filter((l) => l.length > 0)
    .map((line, i) => makeRow({ [column]: line }, i));
}

// Tab-separated (spreadsheet) paste. If the first line matches known header
// aliases it is treated as a header row and mapped; otherwise columns are
// exposed positionally for caller review (no dangerous fuzzy mapping).
export function isTabular(text) {
  const first = String(text ?? "").replace(/\r\n/g, "\n").split("\n")[0] || "";
  return first.includes("\t");
}

export function mapHeaders(headers, aliasMap) {
  // aliasMap: { fieldKey: [aliases...] }. Returns { index -> fieldKey | null }.
  const norm = (s) => String(s || "").trim().toLowerCase();
  const lookup = {};
  for (const [field, aliases] of Object.entries(aliasMap || {})) {
    for (const a of [field, ...aliases]) lookup[norm(a)] = field;
  }
  const mapping = {};
  const ambiguous = [];
  headers.forEach((h, i) => {
    const f = lookup[norm(h)];
    mapping[i] = f || null;
    if (!f) ambiguous.push({ index: i, header: h });
  });
  return { mapping, ambiguous };
}

export function parseTabular(text, { aliasMap = {}, hasHeader = "auto" } = {}) {
  const lines = String(text ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n")
    .split("\n").filter((l) => l.trim().length > 0);
  if (lines.length === 0) return { headers: [], mapping: {}, ambiguous: [], rows: [] };
  const cells = lines.map((l) => l.split("\t").map((c) => c.trim()));
  const firstAsHeader = hasHeader === true ||
    (hasHeader === "auto" && cells[0].some((c) => Object.keys(mapHeaders([c], aliasMap).mapping).length &&
      mapHeaders(cells[0], aliasMap).mapping[cells[0].indexOf(c)]));
  let headers = [];
  let dataRows = cells;
  let mapping = {};
  let ambiguous = [];
  if (firstAsHeader) {
    headers = cells[0];
    dataRows = cells.slice(1);
    ({ mapping, ambiguous } = mapHeaders(headers, aliasMap));
  }
  const rows = dataRows.map((cols, i) => {
    const values = {};
    cols.forEach((v, ci) => {
      const key = firstAsHeader ? (mapping[ci] || `col${ci}`) : `col${ci}`;
      values[key] = v;
    });
    return makeRow(values, i);
  });
  return { headers, mapping, ambiguous, rows };
}

// --- Grid ops ------------------------------------------------------------
export const addRow = (rows, values = {}) => [...rows, makeRow(values, rows.length)];
export const removeRow = (rows, rowId) => rows.filter((r) => r.rowId !== rowId).map((r, i) => ({ ...r, position: i }));
export const duplicateRow = (rows, rowId) => {
  const idx = rows.findIndex((r) => r.rowId === rowId);
  if (idx < 0) return rows;
  const copy = makeRow({ ...rows[idx].values }, idx + 1);
  const next = [...rows.slice(0, idx + 1), copy, ...rows.slice(idx + 1)];
  return next.map((r, i) => ({ ...r, position: i }));
};
export const clearAll = () => [];

// --- Validation plumbing -------------------------------------------------
// validateRow: (values) => { errors:{field:msg}, warnings:[...] }  (module-supplied)
export function applyValidation(rows, validateRow) {
  return rows.map((r) => {
    if (r.status === ROW_STATUS.CREATED) return r; // never re-validate created rows
    const { errors = {}, warnings = [] } = validateRow ? validateRow(r.values, r) : {};
    let status = ROW_STATUS.VALID;
    if (Object.keys(errors).length) status = ROW_STATUS.INVALID;
    else if (warnings.length) status = ROW_STATUS.WARNING;
    return { ...r, errors, warnings, status };
  });
}

// --- Review summary ------------------------------------------------------
export function summarize(rows) {
  const submittable = rows.filter((r) => ![ROW_STATUS.CREATED, ROW_STATUS.INVALID].includes(r.status));
  return {
    total: rows.length,
    ready: rows.filter((r) => r.status === ROW_STATUS.VALID).length,
    warning: rows.filter((r) => r.status === ROW_STATUS.WARNING).length,
    invalid: rows.filter((r) => r.status === ROW_STATUS.INVALID).length,
    duplicates: rows.filter((r) => (r.warnings || []).some((w) => /duplicate/i.test(w))).length,
    created: rows.filter((r) => r.status === ROW_STATUS.CREATED).length,
    submittableCount: submittable.length,
  };
}

// Rows to send to the server: everything not already created and not invalid.
export function submittableRows(rows) {
  return rows.filter((r) => ![ROW_STATUS.CREATED, ROW_STATUS.INVALID].includes(r.status));
}

// --- Batch size ----------------------------------------------------------
export function validateBatchSize(count, max = DEFAULT_MAX_BATCH) {
  if (count <= 0) return { ok: false, code: "empty", message: "No rows to submit." };
  if (count > max) return { ok: false, code: "too_large", message: `Batch too large (max ${max}).` };
  return { ok: true };
}

// --- Result reconciliation ----------------------------------------------
// results: [{ index, status, record_id, code, message }] where index refers to
// the position within the submitted subset (deterministic order).
export function reconcile(rows, submitted, results) {
  const byIndex = new Map(results.map((r) => [r.index, r]));
  const submittedIds = new Set(submitted.map((r) => r.rowId));
  return rows.map((r) => {
    if (!submittedIds.has(r.rowId)) return r; // untouched (e.g. previously created)
    const idx = submitted.findIndex((s) => s.rowId === r.rowId);
    const res = byIndex.get(idx);
    if (!res) return r;
    const map = {
      created: ROW_STATUS.CREATED, duplicate: ROW_STATUS.DUPLICATE,
      validation_error: ROW_STATUS.INVALID, permission_error: ROW_STATUS.FAILED,
      failed: ROW_STATUS.FAILED,
    };
    const status = map[res.status] || ROW_STATUS.FAILED;
    const errors = res.status === "validation_error" && res.message ? { _row: res.message } : r.errors;
    return { ...r, status, errors, result: { record_id: res.record_id || null, code: res.code || null, message: res.message || null } };
  });
}

// --- Reusable pre-submit duplicate marking (client-side, advisory) -------
// Normalizes text (collapse whitespace + lowercase) — deliberately NOT fuzzy, so
// "Check fridge temperature" and "…temperatures" stay distinct. Marks a row as a
// possible duplicate if its `field` matches an already-existing value OR an earlier
// row in the batch. Backend remains authoritative. Reusable by Prep/Supplier batches.
export function normalizeText(s) {
  return String(s ?? "").replace(/\s+/g, " ").trim().toLowerCase();
}

export function markDuplicates(rows, { field, existing = [], normalize = normalizeText, message = "Possible duplicate" }) {
  const existingSet = new Set(existing.map(normalize).filter(Boolean));
  const seen = new Set();
  return rows.map((r) => {
    if (r.status === ROW_STATUS.CREATED || r.status === ROW_STATUS.DUPLICATE) return r;
    const key = normalize(r.values[field]);
    const warnings = (r.warnings || []).filter((w) => w !== message);
    const dup = !!key && (existingSet.has(key) || seen.has(key));
    if (key) seen.add(key);
    if (dup) {
      const status = r.status === ROW_STATUS.INVALID ? r.status : ROW_STATUS.WARNING;
      return { ...r, warnings: [...warnings, message], status };
    }
    return { ...r, warnings };
  });
}
